import * as React from 'react';

import { cn } from '@crm/shared/lib/styles/utils';
import { Button } from '@crm/shared/ui/Button';
import { Textarea } from '@crm/shared/ui/Textarea';

import { Input } from './Input';
import {
    type InputGroupAddonVariants,
    inputGroupAddonVariants,
    type InputGroupButtonVariants,
    inputGroupButtonVariants,
} from './input-group.variants';

const InputGroup = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            data-slot="input-group"
            role="group"
            className={cn(
                'group/input-group relative flex h-8 w-full min-w-0 items-center rounded-lg border border-input transition-colors outline-none in-data-[slot=combobox-content]:focus-within:border-inherit in-data-[slot=combobox-content]:focus-within:ring-0 has-disabled:bg-input/50 has-disabled:opacity-50 has-[[data-slot=input-group-control]:focus-visible]:border-smtr-main-accent-100 has-[[data-slot=input-group-control]:focus-visible]:ring-0 has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50 has-[[data-slot][aria-invalid=true]]:border-destructive has-[[data-slot][aria-invalid=true]]:ring-0 has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>textarea]:h-auto dark:bg-input/30 dark:has-disabled:bg-input/80 dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40 has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3 has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5',
                className
            )}
            {...props}
        />
    )
);

InputGroup.displayName = 'InputGroup';

const InputGroupAddon = ({
    className,
    align = 'inline-start',
    ...props
}: React.ComponentProps<'div'> & InputGroupAddonVariants) => (
    <div
        role="group"
        data-slot="input-group-addon"
        data-align={align}
        className={cn(inputGroupAddonVariants({ align }), className)}
        onClick={(e) => {
            const target = e.target as HTMLElement;

            if (!e.currentTarget.contains(target) || target.closest('button')) {
                return;
            }
            e.currentTarget.parentElement?.querySelector('input')?.focus();
        }}
        {...props}
    />
);

const InputGroupButton = React.forwardRef<
    React.ElementRef<typeof Button>,
    Omit<React.ComponentProps<typeof Button>, 'size' | 'type'> &
        InputGroupButtonVariants & {
            type?: 'button' | 'submit' | 'reset';
        }
>(({ className, type = 'button', variant = 'ghost', size = 'xs', ...props }, ref) => (
    <Button
        ref={ref}
        type={type}
        data-size={size}
        variant={variant}
        className={cn(inputGroupButtonVariants({ size }), className)}
        {...props}
    />
));

InputGroupButton.displayName = 'InputGroupButton';

const InputGroupText = ({ className, ...props }: React.ComponentProps<'span'>) => (
    <span
        className={cn(
            "flex items-center gap-2 text-sm text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
            className
        )}
        {...props}
    />
);

const InputGroupInput = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
    ({ className, ...props }, ref) => (
        <Input
            ref={ref}
            data-slot="input-group-control"
            className={cn(
                'flex-1 rounded-none border-0 bg-transparent shadow-none ring-0 placeholder:text-smtr-main-tertiary-100 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent',
                className
            )}
            {...props}
        />
    )
);

InputGroupInput.displayName = 'InputGroupInput';

const InputGroupTextarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<'textarea'>>(
    ({ className, ...props }, ref) => (
        <Textarea
            ref={ref}
            data-slot="input-group-control"
            className={cn(
                'flex-1 resize-none rounded-none border-0 bg-transparent py-2 shadow-none ring-0 placeholder:text-smtr-main-tertiary-100 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:bg-transparent dark:disabled:bg-transparent ',
                className
            )}
            {...props}
        />
    )
);

InputGroupTextarea.displayName = 'InputGroupTextarea';

export {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupText,
    InputGroupInput,
    InputGroupTextarea,
};
